import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Download, Search, ExternalLink, BadgeCheck } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  adminApi,
  type AdminCharityRow,
  type ListCharitiesInput,
} from "@/lib/api";
import { LIST_STALE_MS, queryKeys } from "@/lib/queries";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/DataTable";
import { StatusPill } from "@/components/StatusPill";
import { downloadCsv } from "@/lib/csv";
import { queryTableProps } from "@/lib/data-table";

export const Route = createFileRoute("/_authed/charities")({
  component: CharitiesPage,
});

const STATUSES = ["", "active", "inactive"] as const;
const VERIFIED = ["", "verified", "unverified"] as const;
const SORTS = [
  { value: "name", label: "Name (A→Z)" },
  { value: "recent", label: "Recently updated" },
  { value: "donations", label: "Largest donations" },
] as const;
const PAGE_SIZE = 50;

function CharitiesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  // Typed filters key a server query: settle them before fetching.
  const debouncedSearch = useDebouncedValue(search);
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("");
  const [verified, setVerified] = useState<(typeof VERIFIED)[number]>("");
  const [sortBy, setSortBy] = useState<(typeof SORTS)[number]["value"]>("name");

  const filters: ListCharitiesInput = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      status: status || undefined,
      verified: verified || undefined,
      sortBy,
      limit: PAGE_SIZE,
    }),
    [debouncedSearch, status, verified, sortBy],
  );

  const query = useInfiniteQuery({
    queryKey: queryKeys.charities.list(filters),
    queryFn: async ({ pageParam }) => {
      const res = await adminApi.listCharities()({ ...filters, cursor: pageParam ?? null });
      return res.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: LIST_STALE_MS,
  });

  const rows = query.data?.pages.flatMap((p) => p.charities) ?? [];
  const totalMatching = query.data?.pages[0]?.totalMatching ?? 0;

  const columns = useMemo<ColumnDef<AdminCharityRow, unknown>[]>(
    () => [
      {
        header: "Name",
        accessorKey: "name",
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="flex items-center gap-1 font-medium">
              {row.original.name}
              {row.original.isVerified ? (
                <BadgeCheck className="h-4 w-4 text-primary" aria-label="Verified" />
              ) : null}
            </span>
            <span className="font-mono text-[10px] text-muted-foreground">
              {row.original.id}
            </span>
          </div>
        ),
      },
      {
        header: "Contact",
        accessorKey: "email",
        cell: ({ row }) =>
          row.original.email ? (
            <span className="text-xs">{row.original.email}</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        header: "Website",
        accessorKey: "website",
        cell: ({ row }) =>
          row.original.website ? (
            <a
              href={
                row.original.website.startsWith("http")
                  ? row.original.website
                  : `https://${row.original.website}`
              }
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.website.replace(/^https?:\/\//, "")}
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        header: "Status",
        accessorKey: "isActive",
        cell: ({ row }) => (
          <div className="flex flex-col gap-1">
            {row.original.isActive ? (
              <StatusPill tone="success">active</StatusPill>
            ) : (
              <StatusPill tone="neutral">inactive</StatusPill>
            )}
            {row.original.isVerified ? (
              <StatusPill tone="info">verified</StatusPill>
            ) : (
              <StatusPill tone="warning">unverified</StatusPill>
            )}
          </div>
        ),
      },
      {
        header: "Donations",
        accessorKey: "totalDonations",
        cell: ({ row }) => (
          <div className="flex flex-col text-xs">
            <span className="font-mono">
              {row.original.totalDonations.toLocaleString()} donations
            </span>
            <span className="font-mono text-muted-foreground">
              £{row.original.totalAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          </div>
        ),
      },
      {
        header: "Items helped",
        accessorKey: "itemsHelped",
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.itemsHelped.toLocaleString()}
          </span>
        ),
      },
      {
        header: "Updated",
        accessorKey: "updatedAt",
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.updatedAt
              ? format(new Date(row.original.updatedAt), "yyyy-MM-dd")
              : "—"}
          </span>
        ),
      },
    ],
    [],
  );

  function exportCsv() {
    downloadCsv(
      `charities-${format(new Date(), "yyyyMMdd-HHmmss")}.csv`,
      rows.map((r) => ({
        id: r.id,
        name: r.name,
        email: r.email ?? "",
        website: r.website ?? "",
        isActive: r.isActive ? "true" : "false",
        isVerified: r.isVerified ? "true" : "false",
        totalDonations: r.totalDonations,
        totalAmount: r.totalAmount,
        itemsHelped: r.itemsHelped,
        updatedAt: r.updatedAt ?? "",
      })),
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Charities</h1>
          <p className="text-sm text-muted-foreground">
            {rows.length.toLocaleString()} of {totalMatching.toLocaleString()}{" "}
            loaded.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-4 md:grid-cols-4">
          <div className="space-y-1 md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">
              Search by name (case-insensitive prefix)
            </label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
                placeholder="Oxfam…"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Status</label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as (typeof STATUSES)[number])}
            >
              {STATUSES.map((s) => (
                <option key={s || "all"} value={s}>
                  {s || "All statuses"}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Verification</label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={verified}
              onChange={(e) => setVerified(e.target.value as (typeof VERIFIED)[number])}
            >
              {VERIFIED.map((v) => (
                <option key={v || "all"} value={v}>
                  {v || "All charities"}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1 md:col-span-4">
            <label className="text-xs font-medium text-muted-foreground">Sort</label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as (typeof SORTS)[number]["value"])}
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <DataTable
        data={rows}
        columns={columns}
        isFetchingMore={query.isFetchingNextPage}
        hasMore={Boolean(query.hasNextPage)}
        onLoadMore={() => void query.fetchNextPage()}
        onRowClick={(r) => navigate({ to: "/charities/$id", params: { id: r.id } })}
        rowKey={(r) => r.id}
        emptyMessage={
          rows.length === 0 && !query.isLoading
            ? "No charities yet. Create one in Firestore — bulk import is on the roadmap."
            : "No matching charities."
        }
        {...queryTableProps(query)}
      />
    </div>
  );
}
